import JsonView from '@uiw/react-json-view';
import Draggable from 'react-draggable';

import { IconWrapper } from '@flexo/general';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import styles from './inspector.module.scss';

const customTheme = {
  '--w-rjv-color': 'rgb(211, 211, 211)',
  '--w-rjv-key-number': 'rgb(211, 211, 211)',
  '--w-rjv-key-string': 'rgb(211, 211, 211)',
  '--w-rjv-background-color': 'transparent',
  '--w-rjv-line-color': 'transparent',
  '--w-rjv-arrow-color': 'rgb(211, 211, 211)',
  '--w-rjv-edit-color': 'rgb(211, 211, 211)',
  '--w-rjv-info-color': 'rgb(211, 211, 211)',
  '--w-rjv-update-color': 'rgb(211, 211, 211)',
  '--w-rjv-copied-color': 'rgb(211, 211, 211)',
  '--w-rjv-copied-success-color': 'rgb(211, 211, 211)',

  '--w-rjv-curlybraces-color': 'rgb(211, 211, 211)',
  '--w-rjv-colon-color': 'rgb(211, 211, 211)',
  '--w-rjv-brackets-color': 'rgb(211, 211, 211)',
  '--w-rjv-ellipsis-color': 'rgb(211, 211, 211)',
  '--w-rjv-quotes-color': 'rgb(211, 211, 211)',
  '--w-rjv-quotes-string-color': 'rgb(211, 211, 211)',

  '--w-rjv-type-string-color': 'rgb(211, 211, 211)',
  '--w-rjv-type-int-color': 'rgb(211, 211, 211)',
  '--w-rjv-type-float-color': 'rgb(211, 211, 211)',
  '--w-rjv-type-bigint-color': 'rgb(211, 211, 211)',
  '--w-rjv-type-boolean-color': 'rgb(211, 211, 211)',
  '--w-rjv-type-date-color': 'rgb(211, 211, 211)',
  '--w-rjv-type-url-color': 'rgb(211, 211, 211)',
  '--w-rjv-type-null-color': 'rgb(211, 211, 211)',
  '--w-rjv-type-nan-color': 'rgb(211, 211, 211)',
  '--w-rjv-type-undefined-color': 'rgb(211, 211, 211)',
};

export function Inspector(props: any) {
  const { setShownInspector, shownInspector, devModeActive, setDevModeActive } =
    props;

  const location = useLocation();
  const pathSegment = location.pathname.split('/')[1];

  const metadata = useSelector((state: any) => state.metadata);
  const kpi = useSelector((state: any) => state.kpi);
  const calendar = useSelector((state: any) => state.calendar);

  const hiveCalendar = calendar?.hiveCalendar;

  let kpiOverviewData = kpi?.site?.['1day']?.[0]?.kpi as any;
  let kpiDetailData = kpi?.site?.['1hour'] as any;

  let rankingData = null;
  let comparisonData = null;

  if (pathSegment?.includes('member')) {
    kpiOverviewData = kpi?.member?.['1day']?.[0]?.kpi as any;
    kpiDetailData = kpi?.member?.['1hour'] as any;
    rankingData = kpi?.memberRanking;
  }

  if (pathSegment?.includes('communit')) {
    kpiOverviewData = kpi?.community?.['1day']?.[0]?.kpi as any;
    kpiDetailData = kpi?.community?.['1hour'] as any;
    rankingData = kpi?.communityRanking;
    comparisonData = kpi?.comparison?.[0]?.kpi as any;
  }

  const [oldInspected, setOldInspected] = useState<any>(null);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [isSilent, setSilent] = useState<boolean>(true);
  const [isUpsideDown, setIsUpsideDown] = useState<null | string>(null);

  const audioRef = useRef<HTMLAudioElement>(null);
  const upsideRef = useRef<HTMLAudioElement>(null);
  const audioClickRef = useRef<HTMLAudioElement>(null);
  const audioFaultRef = useRef<HTMLAudioElement>(null);
  const audioCloseRef = useRef<HTMLAudioElement>(null);

  function closeInspector() {
    setShownInspector(null);
    if (audioCloseRef.current) {
      audioCloseRef.current.play();
    }
    setTimeout(() => {
      setSilent(true);
      setDevModeActive(false);
    }, 230);
  }

  function upsideDown() {
    if (isUpsideDown === null || isUpsideDown === 'UP') {
      setIsUpsideDown('DOWN');
    } else {
      setIsUpsideDown('UP');
    }
  }

  function formatData() {
    return {
      metadata,
      kpi: {
        data: {
          overViewData: kpiOverviewData || null,
          detailData: kpiDetailData || null,
        },
        ranking: rankingData || null,
        comparisonData: comparisonData || null,
      },
      calendar: hiveCalendar,
    };
  }

  useEffect(() => {
    if (
      shownInspector !== null &&
      shownInspector?.devID !== oldInspected?.devID
    ) {
      setOldInspected(shownInspector);
      if (audioClickRef.current) {
        audioClickRef.current.play();
      }
    } else {
      if (audioFaultRef.current && devModeActive === true) {
        audioFaultRef.current.play();
      }
    }
  }, [shownInspector]);

  useEffect(() => {
    if (devModeActive && isSilent === false) {
      if (isUpsideDown === 'DOWN') {
        if (upsideRef.current) {
          upsideRef.current.play();
        }
      } else {
        if (audioRef.current) {
          audioRef.current.play();
        }
      }
    } else {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      if (upsideRef.current) {
        upsideRef.current.pause();
        upsideRef.current.currentTime = 0;
      }
    }
  }, [devModeActive]);

  useEffect(() => {
    if (devModeActive && isSilent === false) {
      if (isUpsideDown === 'DOWN') {
        if (upsideRef.current) {
          upsideRef.current.play();
        }
      } else {
        if (audioRef.current) {
          audioRef.current.play();
        }
      }
    } else {
      if (audioRef.current) {
        audioRef.current.pause();
      }
      if (upsideRef.current) {
        upsideRef.current.pause();
      }
    }
  }, [isSilent]);

  useEffect(() => {
    if (devModeActive && isSilent === false) {
      if (isUpsideDown === 'DOWN') {
        if (audioRef.current) {
          audioRef.current.pause();
        }
        if (upsideRef.current) {
          upsideRef.current.play();
        }
      } else {
        if (upsideRef.current) {
          upsideRef.current.pause();
        }
        if (audioRef.current) {
          audioRef.current.play();
        }
      }
    }
  }, [isUpsideDown]);

  // Aggiunta dell'evento keydown
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // event.preventDefault();

      if (event.ctrlKey && event.altKey && event.metaKey) {
        if (devModeActive === true) {
          closeInspector();
        } else {
          setDevModeActive(true);
        }
      }
    };

    // Aggiungi l'evento
    document.addEventListener('keydown', handleKeyDown);

    // Rimuovi l'evento quando il componente viene smontato
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [devModeActive, setDevModeActive]);

  return devModeActive ? (
    <Draggable
      handle={'.handle'}
      onMouseDown={() => setIsDragging(true)}
      onStop={() => setIsDragging(false)}
    >
      <div className={styles.Inspector}>
        <div
          className={` ${styles.Inspector__Container} ${
            !isUpsideDown
              ? ''
              : isUpsideDown === 'UP'
              ? styles.Inspector__Container__Up
              : styles.Inspector__Container__Down
          }`}
          style={{
            padding: 2,
            height: '100%',
            border: 'solid 3px #fff',
            borderRadius: 8,
            boxSizing: 'border-box',
            paddingBottom: 10,
          }}
        >
          {/* <div style={{ padding: 2, height: '100%', border: 'solid 3px #fff', borderRadius: 8, boxSizing: 'border-box', paddingBottom: 10 }}> */}
          <div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                flexDirection: 'row',
                alignItems: 'start',
                width: '100%',
                height: 60,
              }}
            >
              <div className={styles.Inspector__Title} style={{}}>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'flex-start',
                    alignItems: 'flex-start',
                    height: 50,
                    width: 200,
                    paddingTop: 14,
                  }}
                >
                  <div
                    className={`handle ${styles.Inspector__Grab} ${
                      isDragging ? styles.Inspector__Grab__Grabbing : {}
                    }`}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      height="25"
                      version="1.2"
                      width="25"
                    >
                      <defs>
                        <filter
                          id="shadow"
                          x="-50%"
                          y="-50%"
                          width="200%"
                          height="200%"
                        >
                          <feDropShadow
                            dx="2"
                            dy="2"
                            stdDeviation="3"
                            floodColor="black"
                          />
                        </filter>
                      </defs>

                      <rect
                        x="1.8"
                        y="1.8"
                        width="20"
                        height="20"
                        fill="none"
                        stroke="#fff"
                        strokeWidth="1.8"
                        filter="url(#shadow)"
                      />

                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="2"
                        width="2"
                        x="7"
                        y="7"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="2"
                        width="2"
                        x="11"
                        y="7"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="2"
                        width="2"
                        x="15"
                        y="7"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="2"
                        width="2"
                        x="7"
                        y="11"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="2"
                        width="2"
                        x="11"
                        y="11"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="2"
                        width="2"
                        x="15"
                        y="11"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="2"
                        width="2"
                        x="7"
                        y="15"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="2"
                        width="2"
                        x="11"
                        y="15"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="2"
                        width="2"
                        x="15"
                        y="15"
                      />
                    </svg>
                  </div>

                  <div
                    className={`${styles.Inspector__Switch}`}
                    onClick={() => upsideDown()}
                  >
                    <IconWrapper iconName="switch" />
                  </div>

                  <div
                    className={`${styles.Inspector__Sound}`}
                    style={{ opacity: isSilent ? 0.5 : 1 }}
                    onClick={() => setSilent(!isSilent)}
                  >
                    <svg height="300" version="1.2" width="300">
                      <filter
                        id="shadow"
                        x="-50%"
                        y="-50%"
                        width="200%"
                        height="200%"
                      >
                        <feDropShadow
                          dx="2"
                          dy="2"
                          stdDeviation="3"
                          floodColor="black"
                        />
                      </filter>
                      <rect
                        x="0"
                        y="0"
                        width="300"
                        height="300"
                        fill="none"
                        stroke="white"
                        strokeWidth="50"
                        filter="url(#shadow)"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="156"
                        y="66"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="162"
                        y="66"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="168"
                        y="66"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="174"
                        y="66"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="180"
                        y="66"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="186"
                        y="66"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="192"
                        y="66"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="156"
                        y="72"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="162"
                        y="72"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="168"
                        y="72"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="174"
                        y="72"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="180"
                        y="72"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="186"
                        y="72"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="192"
                        y="72"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="144"
                        y="78"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="150"
                        y="78"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="156"
                        y="78"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="162"
                        y="78"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="180"
                        y="78"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="186"
                        y="78"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="192"
                        y="78"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="144"
                        y="84"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="150"
                        y="84"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="156"
                        y="84"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="162"
                        y="84"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="180"
                        y="84"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="186"
                        y="84"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="192"
                        y="84"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="90"
                        y="90"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="96"
                        y="90"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="102"
                        y="90"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="108"
                        y="90"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="114"
                        y="90"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="120"
                        y="90"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="126"
                        y="90"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="132"
                        y="90"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="138"
                        y="90"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="144"
                        y="90"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="150"
                        y="90"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="180"
                        y="90"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="186"
                        y="90"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="192"
                        y="90"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="90"
                        y="96"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="96"
                        y="96"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="102"
                        y="96"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="108"
                        y="96"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="114"
                        y="96"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="120"
                        y="96"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="126"
                        y="96"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="132"
                        y="96"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="138"
                        y="96"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="144"
                        y="96"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="150"
                        y="96"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="180"
                        y="96"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="186"
                        y="96"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="192"
                        y="96"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="78"
                        y="102"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="84"
                        y="102"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="90"
                        y="102"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="96"
                        y="102"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="132"
                        y="102"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="138"
                        y="102"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="180"
                        y="102"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="186"
                        y="102"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="192"
                        y="102"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="78"
                        y="108"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="84"
                        y="108"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="90"
                        y="108"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="96"
                        y="108"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="132"
                        y="108"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="138"
                        y="108"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="180"
                        y="108"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="186"
                        y="108"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="192"
                        y="108"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="78"
                        y="114"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="84"
                        y="114"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="90"
                        y="114"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="96"
                        y="114"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="132"
                        y="114"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="138"
                        y="114"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="180"
                        y="114"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="186"
                        y="114"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="192"
                        y="114"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="66"
                        y="120"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="72"
                        y="120"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="78"
                        y="120"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="84"
                        y="120"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="132"
                        y="120"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="138"
                        y="120"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="180"
                        y="120"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="186"
                        y="120"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="192"
                        y="120"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="210"
                        y="120"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="216"
                        y="120"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="66"
                        y="126"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="72"
                        y="126"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="78"
                        y="126"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="84"
                        y="126"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="132"
                        y="126"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="138"
                        y="126"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="180"
                        y="126"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="186"
                        y="126"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="192"
                        y="126"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="210"
                        y="126"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="216"
                        y="126"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="66"
                        y="132"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="72"
                        y="132"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="132"
                        y="132"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="138"
                        y="132"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="180"
                        y="132"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="186"
                        y="132"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="192"
                        y="132"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="222"
                        y="132"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="228"
                        y="132"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="66"
                        y="138"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="72"
                        y="138"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="132"
                        y="138"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="138"
                        y="138"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="180"
                        y="138"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="186"
                        y="138"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="192"
                        y="138"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="222"
                        y="138"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="228"
                        y="138"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="66"
                        y="144"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="72"
                        y="144"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="132"
                        y="144"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="138"
                        y="144"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="180"
                        y="144"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="186"
                        y="144"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="192"
                        y="144"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="222"
                        y="144"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="228"
                        y="144"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="66"
                        y="150"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="72"
                        y="150"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="132"
                        y="150"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="138"
                        y="150"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="180"
                        y="150"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="186"
                        y="150"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="192"
                        y="150"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="222"
                        y="150"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="228"
                        y="150"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="66"
                        y="156"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="72"
                        y="156"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="132"
                        y="156"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="138"
                        y="156"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="180"
                        y="156"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="186"
                        y="156"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="192"
                        y="156"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="222"
                        y="156"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="228"
                        y="156"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="66"
                        y="162"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="72"
                        y="162"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="132"
                        y="162"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="138"
                        y="162"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="180"
                        y="162"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="186"
                        y="162"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="192"
                        y="162"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="222"
                        y="162"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="228"
                        y="162"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="66"
                        y="168"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="72"
                        y="168"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="78"
                        y="168"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="84"
                        y="168"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="180"
                        y="168"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="186"
                        y="168"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="192"
                        y="168"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="210"
                        y="168"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="216"
                        y="168"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="66"
                        y="174"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="72"
                        y="174"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="78"
                        y="174"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="84"
                        y="174"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="180"
                        y="174"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="186"
                        y="174"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="192"
                        y="174"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="210"
                        y="174"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="216"
                        y="174"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="78"
                        y="180"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="84"
                        y="180"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="90"
                        y="180"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="96"
                        y="180"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="180"
                        y="180"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="186"
                        y="180"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="192"
                        y="180"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="78"
                        y="186"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="84"
                        y="186"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="90"
                        y="186"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="96"
                        y="186"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="180"
                        y="186"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="186"
                        y="186"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="192"
                        y="186"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="78"
                        y="192"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="84"
                        y="192"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="90"
                        y="192"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="96"
                        y="192"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="180"
                        y="192"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="186"
                        y="192"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="192"
                        y="192"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="90"
                        y="198"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="96"
                        y="198"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="102"
                        y="198"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="108"
                        y="198"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="114"
                        y="198"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="120"
                        y="198"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="126"
                        y="198"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="132"
                        y="198"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="138"
                        y="198"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="144"
                        y="198"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="150"
                        y="198"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="180"
                        y="198"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="186"
                        y="198"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="192"
                        y="198"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="90"
                        y="204"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="96"
                        y="204"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="102"
                        y="204"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="108"
                        y="204"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="114"
                        y="204"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="120"
                        y="204"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="126"
                        y="204"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="132"
                        y="204"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="138"
                        y="204"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="144"
                        y="204"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="150"
                        y="204"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="180"
                        y="204"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="186"
                        y="204"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="192"
                        y="204"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="144"
                        y="210"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="150"
                        y="210"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="156"
                        y="210"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="162"
                        y="210"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="180"
                        y="210"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="186"
                        y="210"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="192"
                        y="210"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="144"
                        y="216"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="150"
                        y="216"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="156"
                        y="216"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="162"
                        y="216"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="180"
                        y="216"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="186"
                        y="216"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="192"
                        y="216"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="156"
                        y="222"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="162"
                        y="222"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="168"
                        y="222"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="174"
                        y="222"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="180"
                        y="222"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="186"
                        y="222"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="192"
                        y="222"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="156"
                        y="228"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="162"
                        y="228"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="168"
                        y="228"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="174"
                        y="228"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="180"
                        y="228"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="186"
                        y="228"
                      />
                      <rect
                        fill="#fff"
                        filter="url(#shadow)"
                        height="6"
                        width="6"
                        x="192"
                        y="228"
                      />
                      {isSilent && (
                        <line
                          x1="50"
                          y1="50"
                          x2="250"
                          y2="250"
                          stroke="white"
                          strokeWidth="16"
                        />
                      )}
                    </svg>
                  </div>
                </div>
              </div>

              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'flex-start',
                  alignItems: 'flex-start',
                }}
              >
                <div
                  className={styles.Inspector__Close}
                  onClick={() => closeInspector()}
                >
                  <svg
                    width="100"
                    height="100"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <defs>
                      <filter
                        id="shadow"
                        x="-50%"
                        y="-50%"
                        width="200%"
                        height="200%"
                      >
                        <feDropShadow
                          dx="5"
                          dy="5"
                          stdDeviation="3"
                          floodColor="black"
                        />
                      </filter>
                    </defs>

                    <rect
                      x="7.5"
                      y="7.5"
                      width="75"
                      height="75"
                      fill="none"
                      stroke="#fff"
                      strokeWidth="6"
                      filter="url(#shadow)"
                    />

                    <rect
                      x="20"
                      y="20"
                      width="10"
                      height="10"
                      fill="#fff"
                      filter="url(#shadow)"
                    />
                    <rect
                      x="30"
                      y="30"
                      width="10"
                      height="10"
                      fill="#fff"
                      filter="url(#shadow)"
                    />
                    <rect
                      x="40"
                      y="40"
                      width="10"
                      height="10"
                      fill="#fff"
                      filter="url(#shadow)"
                    />
                    <rect
                      x="50"
                      y="50"
                      width="10"
                      height="10"
                      fill="#fff"
                      filter="url(#shadow)"
                    />
                    <rect
                      x="60"
                      y="60"
                      width="10"
                      height="10"
                      fill="#fff"
                      filter="url(#shadow)"
                    />
                    <rect
                      x="60"
                      y="20"
                      width="10"
                      height="10"
                      fill="#fff"
                      filter="url(#shadow)"
                    />
                    <rect
                      x="50"
                      y="30"
                      width="10"
                      height="10"
                      fill="#fff"
                      filter="url(#shadow)"
                    />
                    <rect
                      x="40"
                      y="40"
                      width="10"
                      height="10"
                      fill="#fff"
                      filter="url(#shadow)"
                    />
                    <rect
                      x="30"
                      y="50"
                      width="10"
                      height="10"
                      fill="#fff"
                      filter="url(#shadow)"
                    />
                    <rect
                      x="20"
                      y="60"
                      width="10"
                      height="10"
                      fill="#fff"
                      filter="url(#shadow)"
                    />
                  </svg>
                </div>
              </div>
            </div>

            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
                alignItems: 'flex-start',
                width: '90%',
                padding: '0 16px',
                fontSize: 14,
                marginBottom: 24,
              }}
            >
              <span
                style={{
                  lineBreak: 'anywhere',
                  marginBottom: 16,
                  ...(isUpsideDown === 'DOWN'
                    ? { transform: 'rotateX( 180deg )' }
                    : {}),
                }}
              >
                {shownInspector?.devID && isUpsideDown !== 'DOWN'
                  ? shownInspector?.devID
                  : 'Inspector'}
              </span>
              {isUpsideDown !== 'DOWN' && (
                <span style={{ lineBreak: 'anywhere' }}>
                  {shownInspector?.devPath || ''}
                </span>
              )}
            </div>

            <div
              className={styles.Inspector__Text}
              style={{
                lineBreak: 'anywhere',
                fontSize: 15,
                width: 450,
                height: 210,
                paddingLeft: 10,
                overflowY: 'scroll',
                marginLeft: 10,
                paddingBottom: 20,
                paddingRight: 20,
              }}
            >
              {isUpsideDown === 'DOWN' ? (
                <JsonView
                  value={formatData()}
                  collapsed={1}
                  style={customTheme as any}
                />
              ) : shownInspector ? (
                <JsonView
                  value={shownInspector}
                  collapsed={1}
                  style={customTheme as any}
                />
              ) : (
                <span>Please click on a widget</span>
              )}
            </div>
          </div>
        </div>
        {/* audio for background */}
        <audio ref={audioRef} loop>
          <source src="prelude-cut.mp3" type="audio/mp3" />
          Your browser does not support the audio element.
        </audio>

        {/* audio for upsidebackground */}
        <audio ref={upsideRef} loop>
          <source src="upside.mp3" type="audio/mp3" />
          Your browser does not support the audio element.
        </audio>

        {/* audio for click */}
        <audio ref={audioClickRef}>
          <source src="menu-move.ogg" type="audio/mp3" />
          Your browser does not support the audio element.
        </audio>

        {/* audio for fault */}
        <audio ref={audioFaultRef}>
          <source src="menu-back.ogg" type="audio/mp3" />
          Your browser does not support the audio element.
        </audio>

        {/* audio for close */}
        <audio ref={audioCloseRef}>
          <source src="junction.ogg" type="audio/mp3" />
          Your browser does not support the audio element.
        </audio>
      </div>
    </Draggable>
  ) : (
    <></>
  );
}
